import React, { useState, useEffect } from "react";
import { Calendar, Clock, AlertCircle, Check, X } from "lucide-react";

/**
 * Kunden-Stornierung-Komponente (Bootstrap)
 * Ermöglicht Kunden, ihren Termin zu stornieren
 */
const CustomerCancel = () => {
  // Lese Props aus data-Attributen
  const rootElement = document.getElementById("customer-cancel-root");
  const appointmentId = rootElement?.dataset.appointmentId;
  const userSID = rootElement?.dataset.userSid;

  const [appointment, setAppointment] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAppointment();
  }, [appointmentId, userSID]);

  const loadAppointment = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/index.php/customer/getAppointmentInfo?aID=${appointmentId}&uSID=${userSID}`
      );
      const data = await response.json();

      if (data.success) {
        setAppointment(data.appointment);
      } else {
        setError(data.error || "Termin konnte nicht geladen werden");
      }
    } catch (error) {
      console.error("Fehler:", error);
      setError("Ein Fehler ist aufgetreten");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Möchten Sie diesen Termin wirklich stornieren?")) {
      return;
    }

    setCancelling(true);
    setError(null);

    try {
      const formData = new URLSearchParams();
      formData.append("aID", appointmentId);
      formData.append("uSID", userSID);
      formData.append("reason", reason);

      const response = await fetch("/index.php/customer/cancelAppointment", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = `/index.php/customer/appointment_cancelled?aID=${appointmentId}&uSID=${userSID}`;
        }, 3000);
      } else {
        setError(data.error || "Termin konnte nicht storniert werden");
      }
    } catch (error) {
      console.error("Fehler:", error);
      setError("Ein Fehler ist aufgetreten");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <p className="text-muted">Lade Termin...</p>
        </div>
      </div>
    );
  }

  if (error && !appointment) {
    return (
      <div className="container py-5">
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          <AlertCircle size={24} className="me-2" />
          <div>
            <h5 className="alert-heading mb-1">Fehler</h5>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container py-5">
        <div className="alert alert-success text-center" role="alert">
          <Check size={48} className="mb-3 text-success" />
          <h4 className="alert-heading">Termin erfolgreich storniert</h4>
          <p>Sie erhalten eine Bestätigungsmail über die Stornierung.</p>
          <hr />
          <p className="mb-0 small text-muted">
            Sie werden automatisch weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: "900px" }}>
      {/* Header */}
      <div className="mb-5 text-center">
        <h1 className="display-5 fw-bold mb-2">Termin stornieren</h1>
        <p className="text-muted">
          Möchten Sie diesen Termin wirklich stornieren?
        </p>
      </div>

      {/* Termindetails */}
      <div
        className="card shadow-sm mb-4"
        style={{ backgroundColor: "#e7f3ff" }}
      >
        <div className="card-body">
          <h5 className="card-title mb-3">Termindetails</h5>
          <div className="row g-3">
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <Calendar size={18} className="me-2 text-primary" />
                <div>
                  <small className="text-muted d-block">Termintyp</small>
                  <strong>{appointment?.type_name}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <Calendar size={18} className="me-2 text-primary" />
                <div>
                  <small className="text-muted d-block">Datum</small>
                  <strong>
                    {appointment?.appointment_date &&
                      new Date(
                        ...appointment.appointment_date
                          .split("-")
                          .map((n, i) => (i === 1 ? n - 1 : n))
                      ).toLocaleDateString("de-DE")}
                  </strong>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <Clock size={18} className="me-2 text-primary" />
                <div>
                  <small className="text-muted d-block">Uhrzeit</small>
                  <strong>
                    {appointment?.start_time} - {appointment?.end_time} Uhr
                  </strong>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <Clock size={18} className="me-2 text-primary" />
                <div>
                  <small className="text-muted d-block">Dauer</small>
                  <strong>{appointment?.duration_minutes} Min</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warnung */}
      <div
        className="card shadow-sm mb-4"
        style={{ borderLeft: "4px solid #ffc107" }}
      >
        <div className="card-body">
          <div className="d-flex align-items-start">
            <AlertCircle
              className="flex-shrink-0 me-3 text-warning"
              size={24}
            />
            <div>
              <h5 className="card-title mb-2">Wichtiger Hinweis</h5>
              <p className="mb-0">
                Wenn Sie diesen Termin stornieren, wird er endgültig gelöscht.
                Sie erhalten eine Bestätigungsmail über die Stornierung.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stornierungsgrund */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <label htmlFor="reason" className="form-label fw-semibold">
            Grund für die Stornierung (optional)
          </label>
          <textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            className="form-control"
            placeholder="Bitte teilen Sie uns mit, warum Sie den Termin stornieren möchten..."
          />
        </div>
      </div>

      {/* Fehler */}
      {error && (
        <div
          className="alert alert-danger d-flex align-items-center mb-4"
          role="alert"
        >
          <AlertCircle size={20} className="me-2" />
          <span>{error}</span>
        </div>
      )}

      {/* Buttons */}
      <div className="d-flex gap-2 justify-content-center mt-5 mb-4">
        <button
          onClick={handleCancel}
          disabled={cancelling}
          className="btn btn-outline-danger btn-lg px-5"
        >
          {cancelling ? "Wird storniert..." : "Termin endgültig stornieren"}
        </button>
        <a
          href={appointment?.calendar_uuid
            ? `/index.php/customer/appointment?calendar_id=${appointment.calendar_uuid}&userSID=${userSID}`
            : "/"
          }
          className="btn btn-outline-secondary btn-lg px-4"
        >
          Abbrechen
        </a>
      </div>

      <p className="text-center text-muted">
        Möchten Sie den Termin lieber verschieben?{" "}
        <a
          href={`/index.php/customer/reschedule?aID=${appointmentId}&uSID=${userSID}`}
          className="text-primary fw-semibold"
        >
          Hier klicken
        </a>
      </p>
    </div>
  );
};

export default CustomerCancel;
